import { ethers } from "ethers";
import { assertFreshWalletScreening } from "../utils/wallet-screening";

const localLocks = new Set<string>();
let activeOperations = 0;
let foregroundRequests = 0;
const backgroundOperations = new Map<
  string,
  { controller: AbortController; done: Promise<void> }
>();
export function hasActiveFlatJoinSplitV2Operation(): boolean {
  return activeOperations > 0 || foregroundRequests > 0;
}

function abortError(): Error {
  return new DOMException("Wallet settlement was cancelled", "AbortError");
}

/**
 * Hold one privacy-account settlement lease across selection, proving,
 * journaling, signing, and finalization.  Web Locks provide cross-tab safety;
 * the in-process fallback still prevents duplicate work in older browsers.
 */
export async function withFlatJoinSplitV2WalletLock<T>(params: {
  chainId: bigint | number;
  poolAddress: string;
  accountAddress: string;
  signal?: AbortSignal;
  priority?: "foreground" | "background";
  run: (signal?: AbortSignal) => Promise<T>;
}): Promise<T> {
  const walletKey = `${BigInt(params.chainId)}:${ethers.getAddress(params.accountAddress).toLowerCase()}`;
  if (params.signal?.aborted) throw abortError();
  if (params.priority !== "background") {
    foregroundRequests++;
    try {
      const background = backgroundOperations.get(walletKey);
      background?.controller.abort();
      // Keep the lease through cancellation cleanup and any uncertain submit.
      await background?.done;
      return await acquireWalletLock(params);
    } finally {
      foregroundRequests--;
    }
  }
  if (
    hasActiveFlatJoinSplitV2Operation() ||
    backgroundOperations.has(walletKey)
  ) {
    throw new Error(
      "Automatic consolidation is waiting for the wallet to be idle",
    );
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  params.signal?.addEventListener("abort", abort, { once: true });
  let complete!: () => void;
  const done = new Promise<void>((resolve) => {
    complete = resolve;
  });
  const background = { controller, done };
  backgroundOperations.set(walletKey, background);
  try {
    return await acquireWalletLock({
      ...params,
      signal: controller.signal,
      run: () => params.run(controller.signal),
    });
  } finally {
    params.signal?.removeEventListener("abort", abort);
    if (backgroundOperations.get(walletKey) === background)
      backgroundOperations.delete(walletKey);
    complete();
  }
}

async function acquireWalletLock<T>(params: {
  chainId: bigint | number;
  poolAddress: string;
  accountAddress: string;
  signal?: AbortSignal;
  run: (signal?: AbortSignal) => Promise<T>;
}): Promise<T> {
  const pool = ethers.getAddress(params.poolAddress).toLowerCase();
  const account = ethers.getAddress(params.accountAddress).toLowerCase();
  const chainId = BigInt(params.chainId);
  if (chainId <= 0n) throw new Error("Wallet settlement chain ID is invalid");
  if (params.signal?.aborted) throw abortError();
  const name = `stealthos:flat-joinsplit-v2:${chainId}:${pool}:${account}`;
  const locks = globalThis.navigator?.locks;
  const run = async () => {
    activeOperations++;
    try {
      return await params.run(params.signal);
    } finally {
      activeOperations--;
    }
  };
  if (locks) {
    return locks.request(
      name,
      {
        mode: "exclusive",
        ifAvailable: true,
        // Web Locks forbids combining signal with ifAvailable. Check aborts
        // before requesting and in the callback instead; once acquired, keep
        // the lease until run settles, including while it handles cancellation.
      },
      async (lock) => {
        if (!lock) {
          throw new Error(
            "Another wallet tab is already preparing a private settlement",
          );
        }
        if (params.signal?.aborted) throw abortError();
        await assertFreshWalletScreening(account, chainId, params.signal);
        if (params.signal?.aborted) throw abortError();
        return run();
      },
    );
  }
  if (localLocks.has(name)) {
    throw new Error("A private settlement is already in progress");
  }
  localLocks.add(name);
  try {
    if (params.signal?.aborted) throw abortError();
    await assertFreshWalletScreening(account, chainId, params.signal);
    if (params.signal?.aborted) throw abortError();
    return await run();
  } finally {
    localLocks.delete(name);
  }
}
